import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Typography
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PrimaryButton from '../../../Common/Button/primary-button.js';
import {
	createTemplate,
	fetchTemplateById,
	checkTemplateNameAvailability
} from '../../../../Services/api-config.js';
import { holdTemplateNames } from '../../../../Store/Redux/file-list-slice.js';
import { updateContent } from '../../../../Store/Redux/ckeditor-slice.js';
import Loading from '../../../Common/LoaderComponent/loader.js';
import customToast from '../../../Common/custom-toast.js';
import { getErrorMessage } from '../../../../utils/util.js';

const CreateNewTemplate = ({ open, onClose }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const params = new URLSearchParams(location.search);
	const [localTemplateName, setLocalTemplateName] = useState('');
	const [showTextField, setShowTextField] = useState(false);
	const [message, setMessage] = useState('');
	const fileID = params.get('fileId');
	const [isLoading, setIsLoading] = useState(false);
	const { showToast } = customToast();

	useEffect(() => {
		const createTemplateAndNavigate = async () => {
			try {
				setIsLoading(true);
				const isValid = await checkContentValidity();
				if (!isValid) {
					return;
				}

				setMessage('Are you sure you want to create a new template.');
			} catch (error) {
				setMessage(error.message);
			} finally {
				setIsLoading(false);
			}
		};

		createTemplateAndNavigate();
	}, []);

	const handleCancelTemplate = () => {
		// If (cancelClickedOnce) {
		onClose(false);
		setShowTextField(false);
		setMessage('');
		// } else {
		//   setCancelClickedOnce(true);
		//   setMessage('Are you sure you want to cancel? Click again to navigate back.');
		// }
	};

	const checkTemplateNameValidity = async (fileName) => {
		try {
			if (fileName.trim() === '') {
				showToast(
					'warning',
					'Template name is empty, please provide a valid name for the template.'
				);
				return false;
			}

			const trimmedFileName = fileName.trim();
			const isNameTooShort = trimmedFileName.length < 3;
			const isNameTooLong = trimmedFileName.length >= 30;

			if (isNameTooShort) {
				showToast(
					'warning',
					'Please provide a name greater than or equal to 3 characters.'
				);
				return false;
			}

			if (isNameTooLong) {
				showToast(
					'warning',
					'Please provide a name less than or equal to 30 characters.'
				);
				return false;
			}

			const response = await checkTemplateNameAvailability(trimmedFileName);

			if (response?.status === 200) {
				return true;
			}

			showToast('error', getErrorMessage(response));
			return false;
		} catch (error) {
			showToast('error', getErrorMessage(error));
			return false;
		}
	};

	const checkContentValidity = async () => {
		try {
			const response = await fetchTemplateById(fileID);
			const isContentAlreadyPresent
				= response.data && response.data.templateId === fileID;

			if (!isContentAlreadyPresent) {
				setMessage(
					'Content in the API is not the same as the current editor. Proceed without saving the current content?'
				);
				return false;
			}

			return true;
		} catch {
			setMessage('An error occurred while checking the content API.');
			return false;
		}
	};

	const handleCreateNewTemplate = async () => {
		if (showTextField) {
			try {
				setIsLoading(true);
				const isValid = await checkTemplateNameValidity(localTemplateName);
				if (!isValid) {
					return;
				}
				/* Not using code from here as reseting the whole canvas if new template is created */

				/* Till here */

				const newTemplateData = {
					templateName: localTemplateName,
					templateHtml: '<p><br data-cke-filler="true"></p>',
					templateThumbnail: '',
					templateConfig: {}
				};

				const response = await createTemplate(newTemplateData);
				if (response.status === 200) {
					navigate(`/edit?createmode=true&fileId=${response.data.templateId}`);
					window.document.querySelector('#editor').innerHTML
						= newTemplateData.templateHtml;
					/* Reload the window to reset the editor canvas */
					window.location.reload();
					dispatch(holdTemplateNames(localTemplateName));
					setShowTextField(false);
					setMessage('');
					onClose(false);
					dispatch(updateContent(''));
				} else {
					setMessage('An error occurred while processing the API request.');
				}
			} catch (error) {
				setMessage(error.message);
			} finally {
				setIsLoading(false);
			}
		} else if (message === 'Are you sure you want to create a new template.') {
			setShowTextField(true);
			setMessage('');
		} else if (
			message === 'Content in the API is not the same as the current editor. Proceed without saving the current content?'
			|| message === 'An error occurred while checking the content API.'
			|| message === 'Are you sure you want to cancel? Click again to navigate back.'
		) {
			onClose(false);
			setMessage('');
		}
	};

	return (
		<>
			<Dialog
				open={open}
				slotProps={{
					paper: {
						style: {
							width: '312px',
							borderRadius: '8px'
						}
					}
				}}
			>
				<DialogTitle>
					{showTextField ? 'Enter new template name' : 'Confirmation'}
				</DialogTitle>
				<DialogContent>
					{showTextField ? (
						<TextField
							margin='dense'
							placeholder='Enter New Template Name'
							slotProps={{ input: { style: { borderRadius: '8px' } } }}
							fullWidth
							maxLength={100}
							value={localTemplateName}
							onChange={(e) => setLocalTemplateName(e.target.value)}
						/>
					) : null}
					{message && (
						<Typography sx={{ color: 'black' }}>{message}</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<PrimaryButton
						value={showTextField ? 'Close' : 'Cancel'}
						backgroundColor='transparent'
						height='40px'
						weight='100px'
						padding='10px 16px'
						border='1px solid #B7D423'
						onClick={handleCancelTemplate}
					/>
					<PrimaryButton
						value={showTextField ? 'Continue' : 'Confirm'}
						backgroundColor='#B7D423'
						height='40px'
						weight='100px'
						padding='10px 16px'
						onClick={handleCreateNewTemplate}
					/>
				</DialogActions>
			</Dialog>
			{isLoading && (
				<Loading open={isLoading} onClose={() => setIsLoading(false)} />
			)}
		</>
	);
};

export default CreateNewTemplate;

CreateNewTemplate.propTypes = {
	open: PropTypes.any,
	onClose: PropTypes.any
};
